import {
  CheckIcon,
  ClipboardDocumentIcon,
  EyeIcon,
  EyeSlashIcon,
} from "@heroicons/react/24/outline";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { GenericError } from "@via/components/error";
import { Spinner } from "@via/components/loading/loading";
import {
  BannerType,
  MessageBanner,
} from "@via/components/message-banner/message-banner";
import Modal from "@via/components/modal";
import TableContextMenu from "@via/components/table/table-context-menu";
import Table, { TableSize } from "@via/components/table/table.tsx";
import { NOTIFICATION } from "@via/context/notifications-context";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import useUrlParams from "@via/hooks/useURLParams";
import EditWorkbenchInstanceModal from "@via/pages/workbenchs/edit-workbench-instance-modal";
import EditTimeoutModal from "@via/pages/workbenchs/extend-timeout-modal";
import LaunchWorkbenchModal from "@via/pages/workbenchs/launch-workbench-modal";
import { getWorkbenchInstanceStatusLabel } from "@via/pages/workbenchs/utils/helper";
import {
  useDeleteWorkbenchInstance,
  useFetchWorkbenchPassword,
  useStartWorkbench,
  useStopWorkbench,
  useTerminateWorkbench,
  useUpdateWorkbenchInstance,
  useWorkbenchsInstanceList,
} from "@via/pages/workbenchs/workbenchs.api";
import {
  ButtonColor,
  EMPTY_TABLE_MESSAGE,
  ERROR,
  ROUTES,
  WorkbenchActions,
} from "@via/utils/constants";
import {
  GCPVMInstanceType,
  ListWorkbenchInstancesResponseBodyItem,
  vmStateForUI,
  WorkbenchInstanceState,
} from "common";
import { useRegisterCortexContext } from "@via/hooks/useRegisterCortexContext.tsx";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Tooltip } from "react-tooltip";
import { useInterval } from "usehooks-ts";
import { useWorkbenchTimeoutAlert } from "@via/hooks/useWorkbenchTimeoutAlert";
import WorkbenchStatusBoxes from "@via/pages/workbenchs/components/workbench-status-boxes";
import WorkbenchFilterBar, {
  StatusFilterValue,
} from "@via/pages/workbenchs/components/workbench-filter-bar";
import { createWorkbenchFilterQuery } from "@via/pages/workbenchs/utils/filter-helper";
import { classNames } from "@via/utils/helpers.tsx";

// Hook to calculate and update countdown timer
const useCountdown = (expiresAt: string | null) => {
  const [countdown, setCountdown] = useState<string>("-");

  const calculateCountdown = () => {
    if (!expiresAt) {
      return "-";
    }

    const now = new Date().getTime();
    const expiry = new Date(expiresAt).getTime();
    const difference = expiry - now;

    if (difference <= 0) {
      return "Expired";
    }

    const days = Math.floor(difference / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );
    const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((difference % (1000 * 60)) / 1000);

    // Format: only show non-zero leading units
    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m ${seconds}s`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  // Calculate immediately on mount or when expiresAt changes
  useEffect(() => {
    setCountdown(calculateCountdown());
  }, [expiresAt]);

  // Update every second
  useInterval(() => {
    setCountdown(calculateCountdown());
  }, 1000);

  return countdown;
};

// Component to display countdown
const CountdownCell = ({ expiresAt }: { expiresAt: string | null }) => {
  const countdown = useCountdown(expiresAt);
  return (
    <span className={countdown === "Expired" ? "text-red-600" : ""}>
      {countdown}
    </span>
  );
};

const formatDateTime = (value?: string | null): string => {
  if (!value) return "—";
  return new Date(value).toLocaleString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const formatUptime = (seconds?: number | null): string => {
  if (!seconds || seconds <= 0) {
    return "0h";
  }
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const parts: string[] = [];
  if (hours > 0) {
    parts.push(`${hours}h`);
  }
  if (minutes > 0) {
    parts.push(`${minutes}m`);
  }
  if (parts.length === 0) {
    parts.push("<1m");
  }
  return parts.join(" ");
};

const formatCostEstimate = (value?: string | null): string => {
  return value ?? "$0.00";
};

const defaultParams = {
  sort: [{ id: "dateModified", desc: true }],
  page: 0,
  pageSize: 10,
};

const REFETCH_INTERVAL = 10000;

const columnHelper =
  createColumnHelper<ListWorkbenchInstancesResponseBodyItem>();

const INLINE_WORKBENCH_ACTIONS = new Set<WorkbenchActions>([
  WorkbenchActions.Open,
  WorkbenchActions.Resume,
  WorkbenchActions.Stop,
  WorkbenchActions.Terminate,
]);

export default function ManageWorkbenchStatus(): JSX.Element {
  const { setUrlParams, getInitialParams } = useUrlParams(defaultParams);
  const initialParams = getInitialParams();
  const { setNotification } = useNotifications();
  const [password, setPassword] = useState(null);
  const [toggleUserPassword, setToggleUserPassword] = useState(false);
  const [copied, setCopied] = useState(false);
  const [page, setPage] = useState(initialParams.page);
  const [pageSize, setPageSize] = useState(initialParams.pageSize);
  const [sort, setSort] = useState<Array<{ id: string; desc: boolean }>>(
    initialParams.sort
  );
  const [
    shouldFetchWorkbenchsInstanceListInInterval,
    setShouldFetchWorkbenchsInstanceListInInterval,
  ] = useState(false);
  const [openLaunchModal, setOpenLaunchModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openEditTimeoutModal, setOpenEditTimeoutModal] = useState(false);
  const [selectedRow, setSelectedRow] =
    useState<ListWorkbenchInstancesResponseBodyItem>();
  const [stopModalWorkbench, setStopModalWorkbench] =
    useState<ListWorkbenchInstancesResponseBodyItem | null>(null);
  const [terminateModalWorkbench, setTerminateModalWorkbench] =
    useState<ListWorkbenchInstancesResponseBodyItem | null>(null);

  // Filter state
  const [selectedInstanceType, setSelectedInstanceType] =
    useState<GCPVMInstanceType | null>(null);
  const [selectedOwner, setSelectedOwner] = useState<string | null>(null);
  const [selectedStatuses, setSelectedStatuses] = useState<StatusFilterValue[]>(
    []
  );
  const [searchTerm, setSearchTerm] = useState("");

  // Create filter query string from filter state
  const filterQuery = useMemo(() => {
    return createWorkbenchFilterQuery({
      instanceType: selectedInstanceType,
      owner: selectedOwner,
      statuses: selectedStatuses,
      search: searchTerm,
    });
  }, [selectedInstanceType, selectedOwner, selectedStatuses, searchTerm]);

  const {
    data: workbenchsInstanceList,
    isError: isWorkbenchsListInstanceError,
    isLoading: isWorkbenchsListInstanceLoading,
  } = useWorkbenchsInstanceList(
    page,
    pageSize,
    sort,
    {
      refetchInterval: shouldFetchWorkbenchsInstanceListInInterval
        ? REFETCH_INTERVAL
        : false,
    },
    filterQuery
  );
  const {
    mutate: terminateWorkbench,
    isPending: isTerminatingWorkbenchLoading,
  } = useTerminateWorkbench();

  const { mutate: stopWorkbench, isPending: isStoppingWorkbenchLoading } =
    useStopWorkbench();

  const { mutate: startWorkbench, isPending: isStartingWorkbenchLoading } =
    useStartWorkbench();

  const { mutate: deleteWorkbench, isPending: isDeletingWorkbenchLoading } =
    useDeleteWorkbenchInstance();

  const {
    mutateAsync: updateWorkbenchInstance,
    isPending: isUpdatingWorkbenchInstanceLoading,
  } = useUpdateWorkbenchInstance();

  const {
    data: workbenchPasswordData,
    mutate: fetchWorkbenchPassword,
    isSuccess: isFetchWorkbenchPasswordSuccess,
  } = useFetchWorkbenchPassword();

  // Monitor workbench timeouts and alert user when expiring
  useWorkbenchTimeoutAlert({
    workbenchInstances: workbenchsInstanceList?.data,
    enabled: true,
    alertThresholdMinutes: 5, // Alert when less than 5 minutes remaining
  });

  useEffect(() => {
    if (workbenchsInstanceList?.data?.length) {
      const fetchCondition = workbenchsInstanceList.data?.some(
        workbench => workbench.status !== WorkbenchInstanceState.Terminated
      );
      setShouldFetchWorkbenchsInstanceListInInterval(fetchCondition);
    }
  }, [workbenchsInstanceList]);

  useEffect(() => {
    if (!openLaunchModal) {
      setSelectedRow(undefined);
    }
  }, [openLaunchModal]);

  useEffect(() => {
    if (!openEditModal) {
      setSelectedRow(undefined);
    }
  }, [openEditModal]);

  useEffect(() => {
    if (!openEditTimeoutModal) {
      setSelectedRow(undefined);
    }
  }, [openEditTimeoutModal]);

  useEffect(() => {
    if (isFetchWorkbenchPasswordSuccess && workbenchPasswordData) {
      const { password } = workbenchPasswordData;
      setPassword(password);
    }
  }, [isFetchWorkbenchPasswordSuccess, workbenchPasswordData]);

  const handleContextMenuAction = useCallback(
    (name: string, row: ListWorkbenchInstancesResponseBodyItem): void => {
      switch (name) {
        case WorkbenchActions.Open:
          window.open(row.accessUrl, "_blank", "noreferrer");
          break;
        case WorkbenchActions.Stop:
          setStopModalWorkbench(row);
          break;
        case WorkbenchActions.Terminate:
          setTerminateModalWorkbench(row);
          break;
        case WorkbenchActions.Resume:
          startWorkbench(row.id);
          break;
        case WorkbenchActions.Delete:
          deleteWorkbench(row.id);
          break;
        case WorkbenchActions.EditTimeout:
          setSelectedRow(row);
          setOpenEditTimeoutModal(true);
          break;
        case WorkbenchActions.Edit:
          setSelectedRow(row);
          setOpenEditModal(true);
          break;
        case WorkbenchActions.RevealPassword:
          fetchWorkbenchPassword(row.id);
          break;
      }
    },
    [startWorkbench, deleteWorkbench, fetchWorkbenchPassword]
  );

  const handleUpdateWorkbenchInstance = (
    id: number,
    data: {
      name?: string | null;
      instanceType?: GCPVMInstanceType;
      diskSize?: number;
    }
  ): Promise<unknown> => updateWorkbenchInstance({ id, body: data });

  const handleConfirmStop = async (): Promise<void> => {
    if (stopModalWorkbench) {
      stopWorkbench(stopModalWorkbench.id);
    }
  };

  const handleConfirmTerminate = async (): Promise<void> => {
    if (terminateModalWorkbench) {
      terminateWorkbench(terminateModalWorkbench.id);
    }
  };

  const handleCopyButtonClick = async e => {
    e.preventDefault();
    if (navigator?.clipboard) {
      if (password) {
        try {
          await navigator.clipboard.writeText(password || "");
          setCopied(true);
          setTimeout(() => setCopied(false), 1000);
        } catch (err) {
          console.error("Failed to copy password to clipboard", err);
        }
      }
    } else {
      setNotification({
        id: "copy-clipboard-not-supported-error",
        message: "Clipboard API",
        details:
          "Clipboard API not supported in this browser. Please copy the text manually or try another browser.",
        variant: NOTIFICATION.ERROR,
      });
    }
  };

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor("id", {
          cell: info => <span>{info.getValue()}</span>,
          header: () => <span>ID</span>,
          size: 80,
        }),
        columnHelper.accessor("workbench", {
          header: () => <span>Workbench</span>,
          cell: ({ row }) => {
            const primary = row.original.name || row.original.workbench;
            return (
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-gray-900">
                  {primary}
                </span>
                <span className="text-xs text-gray-500">
                  {row.original.workbench}
                </span>
              </div>
            );
          },
        }),
        columnHelper.accessor("status", {
          header: () => <span>Status</span>,
          cell: ({ row }) => {
            const instance = row.original;
            return (
              <div className="flex flex-col gap-2">
                <div>{getWorkbenchInstanceStatusLabel(instance.status)}</div>
                <div className="text-xs text-gray-500 space-y-1">
                  <div>Uptime: {formatUptime(instance.uptimeSeconds)}</div>
                  <div>
                    Approx. Cost: {formatCostEstimate(instance.accumulatedCost)}
                  </div>
                  <div>Updated: {formatDateTime(instance.updatedAt)}</div>
                  <div>Created: {formatDateTime(instance.dateCreated)}</div>
                </div>
              </div>
            );
          },
          size: 220,
        }),
        columnHelper.accessor("timeout", {
          header: () => <span>Expiry Timeout</span>,
          cell: ({ row }) => (
            <CountdownCell
              expiresAt={row.original.timeout?.expiresAt || null}
            />
          ),
          size: 140,
          enableSorting: false,
        }),
        columnHelper.accessor("instanceType", {
          header: () => <span>Instance</span>,
          cell: ({ row }) => (
            <div className="flex flex-col">
              <span className="text-sm text-gray-900">
                {row.original.instanceType || "—"}
              </span>
              <span className="text-xs text-gray-500">
                {row.original.diskSize ? `${row.original.diskSize} GB` : "—"}
              </span>
            </div>
          ),
        }),
        columnHelper.display({
          id: "owner",
          header: () => <span>Owner</span>,
          cell: ({ row }) => (
            <span className="text-sm text-gray-900">
              {row.original.owner.username}
            </span>
          ),
          size: 200,
        }),
        columnHelper.accessor("id", {
          enableSorting: false,
          cell: ({ row }) => {
            const { status } = row.original;
            const options = {
              [WorkbenchInstanceState.Running]: [
                WorkbenchActions.Open,
                WorkbenchActions.EditTimeout,
                WorkbenchActions.Edit,
                WorkbenchActions.Stop,
                WorkbenchActions.Terminate,
              ],
              [WorkbenchInstanceState.Suspended]: [
                WorkbenchActions.Edit,
                WorkbenchActions.Resume,
                WorkbenchActions.Terminate,
              ],
              [WorkbenchInstanceState.Terminated]: [
                WorkbenchActions.Edit,
                WorkbenchActions.Resume,
                WorkbenchActions.Terminate,
              ],
              [WorkbenchInstanceState.Deleted]: [WorkbenchActions.Delete],
              [WorkbenchInstanceState.Failed]: [WorkbenchActions.Delete],
            }[status];

            if (!options) {
              return <Spinner loading>{vmStateForUI(status || "")}</Spinner>;
            }

            const inlineButtons = options.filter(name =>
              INLINE_WORKBENCH_ACTIONS.has(name)
            );
            const menuActions = options.filter(
              name => !INLINE_WORKBENCH_ACTIONS.has(name)
            );

            if (status !== WorkbenchInstanceState.Deleted) {
              menuActions.push(WorkbenchActions.RevealPassword);
            }

            return (
              <div className="flex items-start justify-between gap-3">
                <div className="flex flex-col items-start gap-2">
                  {inlineButtons.map(name => {
                    const isTerminate = name === WorkbenchActions.Terminate;
                    return (
                      <button
                        key={name}
                        type="button"
                        className={classNames(
                          "rounded-full border px-3 py-1 text-xs font-medium",
                          isTerminate
                            ? "border-red-200 text-red-500 hover:border-red-300"
                            : "border-gray-200 text-gray-700 hover:border-gray-300"
                        )}
                        onClick={() =>
                          handleContextMenuAction(name, row.original)
                        }>
                        {name}
                      </button>
                    );
                  })}
                </div>
                {menuActions.length ? (
                  <TableContextMenu
                    className="mt-1"
                    options={menuActions.map(name => ({ name, href: "#" }))}
                    onSelect={option =>
                      handleContextMenuAction(option.name, row.original)
                    }
                  />
                ) : null}
              </div>
            );
          },
          header: () => "Options",
          size: 150,
        }),
      ] as Array<ColumnDef<ListWorkbenchInstancesResponseBodyItem>>,
    [handleContextMenuAction]
  );

  useEffect(() => {
    setUrlParams({ sort, page, pageSize });
  }, [sort, page, pageSize]);

  const getManageWorkbenchStatusContext = useCallback(() => {
    const instances = workbenchsInstanceList?.data || [];
    const metadata = workbenchsInstanceList?.metadata || {
      totals: {
        accessible: 0,
        running: 0,
        stopped: 0,
        terminated: 0,
        totalCost: "$0.00",
      },
      owners: [],
    };
    const totals = metadata.totals;
    const owners = metadata.owners;

    return {
      workbenchInstances: instances.map(instance => ({
        id: instance.id,
        workbench: instance.workbench,
        name: instance.name,
        status: instance.status,
        timeout: instance.timeout
          ? {
              expiresAt: instance.timeout.expiresAt,
            }
          : null,
        accumulatedCost: instance.accumulatedCost,
        vmLastStateChangedAt: instance.vmLastStateChangedAt,
        dateCreated: instance.dateCreated,
        owner: instance.owner?.username,
        instanceType: instance.instanceType,
        diskSize: instance.diskSize,
      })),
      total: workbenchsInstanceList?.total || 0,
      currentPage: page,
      pageSize: pageSize,
      sortBy: sort.length > 0 ? sort[0] : null,
      statusCounts: instances.reduce(
        (acc, instance) => {
          const status = instance.status || "unknown";
          acc[status] = (acc[status] || 0) + 1;
          return acc;
        },
        {} as Record<string, number>
      ),
      totals,
      owners,
    };
  }, [
    workbenchsInstanceList?.data,
    workbenchsInstanceList?.total,
    page,
    pageSize,
    sort,
  ]);

  useRegisterCortexContext({
    routePattern: ROUTES.WORKBENCH_MANAGE_STATUS,
    contextName: "Manage Workbench Status",
    getContext: getManageWorkbenchStatusContext,
    systemPrompt:
      "You are analyzing workbench instances management page. Help answer questions about workbench instances, their status, timeouts, costs, and assist with filtering and sorting.",
    enabled: !!workbenchsInstanceList,
  });

  // Handle status filter change from dropdown
  const handleStatusFilterChange = (statuses: StatusFilterValue[]) => {
    setSelectedStatuses(statuses);
    setPage(0);
  };

  // Handle instance type change
  const handleInstanceTypeChange = (instanceType: GCPVMInstanceType | null) => {
    setSelectedInstanceType(instanceType);
    setPage(0);
  };

  // Handle owner change
  const handleOwnerChange = (owner: string | null) => {
    setSelectedOwner(owner);
    setPage(0);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setPage(0);
  };

  // Handle clear all filters
  const handleClearAllFilters = () => {
    setSelectedInstanceType(null);
    setSelectedOwner(null);
    setSelectedStatuses([]);
    setSearchTerm("");
    setPage(0);
  };

  return (
    <main className="py-4">
      {isWorkbenchsListInstanceError ? (
        <div className="via-generic-error">
          <GenericError message={ERROR} />
        </div>
      ) : (
        <>
          {workbenchsInstanceList?.data?.length ? (
            <div className="mb-4 -mt-4">
              <MessageBanner
                messages={[
                  "Each workbench runs on a dedicated instance with its own resources.",
                  "Stop: Shuts down the instance. You won't be charged for CPU or memory while it's stopped, but storage costs still apply for the disk you've selected. You can resume later and your files will be preserved.",
                  "Terminate: Permanently deletes the instance and all associated data. This action cannot be undone and ends all charges, including storage.",
                  "Allow browser notifications to be alerted before your workbench expires.",
                ]}
                type={BannerType.Warning}
              />
            </div>
          ) : null}
          {workbenchsInstanceList?.data !== undefined ? (
            <WorkbenchStatusBoxes metadata={workbenchsInstanceList?.metadata} />
          ) : null}
          {workbenchsInstanceList?.data !== undefined ? (
            <WorkbenchFilterBar
              owners={workbenchsInstanceList?.metadata?.owners || []}
              selectedInstanceType={selectedInstanceType}
              selectedOwner={selectedOwner}
              selectedStatuses={selectedStatuses}
              searchTerm={searchTerm}
              onInstanceTypeChange={handleInstanceTypeChange}
              onOwnerChange={handleOwnerChange}
              onStatusChange={handleStatusFilterChange}
              onSearchChange={handleSearchChange}
              onClearAll={handleClearAllFilters}
            />
          ) : null}
          <Table
            isLoading={
              isWorkbenchsListInstanceLoading ||
              isTerminatingWorkbenchLoading ||
              isStoppingWorkbenchLoading ||
              isStartingWorkbenchLoading ||
              isDeletingWorkbenchLoading ||
              (!openEditModal && isUpdatingWorkbenchInstanceLoading)
            }
            data={workbenchsInstanceList?.data}
            columns={columns}
            sort={sort}
            setSort={setSort}
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            applyColumnSize
            total={workbenchsInstanceList?.total}
            emptyMessage={EMPTY_TABLE_MESSAGE.WORKBENCH_INSTANCE_LIST}
            size={TableSize.Big}
          />
        </>
      )}
      {openLaunchModal && (
        <LaunchWorkbenchModal
          workbenchId={selectedRow!.id}
          open={openLaunchModal}
          setOpen={() => setOpenLaunchModal(false)}
        />
      )}
      {openEditModal && selectedRow && (
        <EditWorkbenchInstanceModal
          open={openEditModal}
          setOpen={setOpenEditModal}
          workbenchId={selectedRow.id}
          currentName={selectedRow.name}
          currentInstanceType={selectedRow.instanceType}
          currentDiskSize={selectedRow.diskSize}
          status={selectedRow.status}
          isUpdating={isUpdatingWorkbenchInstanceLoading}
          onSubmit={handleUpdateWorkbenchInstance}
        />
      )}
      {openEditTimeoutModal && selectedRow && (
        <EditTimeoutModal
          open={openEditTimeoutModal}
          setOpen={setOpenEditTimeoutModal}
          workbenchId={selectedRow.id}
          currentTimeoutExpiresAt={selectedRow.timeout?.expiresAt ?? null}
        />
      )}
      <Modal
        title="Password"
        open={!!password}
        setOpen={() => {
          setPassword(null);
          setToggleUserPassword(false);
        }}
        hasCancelButton={false}>
        <>
          <div className="w-full my-2">
            <label className="flex flex-row items-center justify-between text-sm font-medium leading-6 text-gray-900 mb-2">
              Password
            </label>
            <div className="relative">
              <input
                className="w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 mb-2 truncate pr-15"
                readOnly
                name="password"
                type={toggleUserPassword ? "text" : "password"}
                value={password || ""}
              />
              <div className="absolute right-2 top-2 flex space-x-2">
                <button
                  type="button"
                  className="text-gray-600 hover:text-gray-900"
                  onClick={() => setToggleUserPassword(!toggleUserPassword)}
                  data-tooltip-id="password-toggle-tooltip">
                  {toggleUserPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
                <button
                  onClick={handleCopyButtonClick}
                  data-tooltip-id="password-copy-tooltip">
                  {copied ? (
                    <CheckIcon className="w-5 h-5 text-green-500" />
                  ) : (
                    <ClipboardDocumentIcon
                      className="h-5 w-5 text-gray-600 hover:text-gray-900"
                      aria-hidden="true"
                    />
                  )}
                </button>
              </div>
            </div>
          </div>
          {password && (
            <>
              <Tooltip
                id="password-toggle-tooltip"
                place="top"
                content={toggleUserPassword ? "Hide password" : "Show password"}
                className="z-10"
              />
              <Tooltip
                id="password-copy-tooltip"
                place="top"
                content={copied ? "Copied!" : "Copy password"}
                className="z-10"
              />
            </>
          )}
        </>
      </Modal>
      <Modal
        open={!!stopModalWorkbench}
        setOpen={() => setStopModalWorkbench(null)}
        title="Pause VM"
        confirmText="Pause"
        isConfirmLoading={isStoppingWorkbenchLoading}
        onConfirm={handleConfirmStop}>
        <div className="flex flex-col py-2 mb-6 text-sm leading-6 text-slate-600 md:py-3 lg:py-4">
          <p>
            Are you sure you want to stop{" "}
            <span className="font-semibold">
              {stopModalWorkbench?.name || stopModalWorkbench?.workbench}
            </span>
            ?
          </p>
          <p className="py-2">
            Any active computations or sessions will end, but the VM can be
            restarted later.
          </p>
        </div>
      </Modal>
      <Modal
        open={!!terminateModalWorkbench}
        setOpen={() => setTerminateModalWorkbench(null)}
        title="Terminate VM"
        confirmText="Terminate"
        confirmButtonColor={ButtonColor.Red}
        isConfirmLoading={isTerminatingWorkbenchLoading}
        onConfirm={handleConfirmTerminate}>
        <div className="flex flex-col py-2 mb-6 text-sm leading-6 text-slate-600 md:py-3 lg:py-4">
          <p>
            Are you sure you want to terminate{" "}
            <span className="font-semibold">
              {terminateModalWorkbench?.name ||
                terminateModalWorkbench?.workbench}
            </span>
            ?
          </p>
          <p className="py-2">
            All running processes will stop immediately and the VM cannot be
            restarted.
          </p>
        </div>
      </Modal>
    </main>
  );
}
